import { useCallback } from 'react';
import { is } from 'ramda';

import { Source } from '@/scenarios/legacy-player/types/config';
import { useLogger } from '@/shared/hooks/useLogger';
import { OnErrorHandler, PlayerApiError } from '@/scenarios/legacy-player/types/error';

import { PlayerProps } from '../types';

const getVsidFromError = (err: PlayerApiError): string | undefined => err?.vsid;

const getErrorMessage = (err: PlayerApiError): string => {
    let errorMessage = 'PlayerError:';

    if (is(Object, err)) {
        if (err.code === 0) {
            errorMessage = `${errorMessage} Player initialization failed`;
        } else {
            errorMessage = `${errorMessage} ${err.code}`;
        }
    } else {
        errorMessage = `${errorMessage} ${JSON.stringify(err)}`;
    }

    return errorMessage;
};

interface UseOnErrorParams {
    contentId: PlayerProps['contentId'];
    onError?: PlayerProps['onError'];
    source?: Source | Source[];
}

export const useOnError = (params: UseOnErrorParams): OnErrorHandler => {
    const { contentId, source, onError } = params;
    const logger = useLogger();

    const handleError = useCallback(
        (err: PlayerApiError) => {
            if (contentId) {
                onError?.(contentId, err);
            }

            const message = getErrorMessage(err);

            const event = {
                message,
                source: 'player',
                additional: {
                    source,
                    vsid: getVsidFromError(err),
                },
                level: 'warn' as const,
                error: err,
            };

            logger.error(event, message);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onError, contentId]
    );

    return handleError;
};
